/**
 * Product Grid Component
 *
 * Responsive product grid with view toggle
 * Task: T4.4.2 - Product Grid component
 */

import {
  Box,
  InlineGrid,
  InlineStack,
  Button,
  Text,
  Spinner,
  BlockStack,
} from '@shopify/polaris';
import { ListBulletedIcon, ViewIcon } from '@shopify/polaris-icons';
import { useState } from 'react';
import { ProductCard, type ProductCardData } from './ProductCard';

interface ProductGridProps {
  products: ProductCardData[];
  loading?: boolean;
  emptyMessage?: string;
  onAddToWishlist?: (productId: string) => void;
  showViewToggle?: boolean;
  columns?: {
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
  };
}

export function ProductGrid({
  products,
  loading = false,
  emptyMessage = 'No products found',
  onAddToWishlist,
  showViewToggle = false,
  columns = { xs: 1, sm: 2, md: 3, lg: 4 },
}: ProductGridProps) {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  if (loading) {
    return (
      <Box padding="800">
        <BlockStack align="center">
          <Spinner size="large" />
          <Text as="p" tone="subdued">Loading products...</Text>
        </BlockStack>
      </Box>
    );
  }

  if (products.length === 0) {
    return (
      <Box padding="800">
        <BlockStack align="center">
          <Text as="p" tone="subdued">{emptyMessage}</Text>
        </BlockStack>
      </Box>
    );
  }

  return (
    <BlockStack gap="400">
      {showViewToggle && (
        <InlineStack align="end" gap="200">
          <Button
            icon={ViewIcon}
            variant={viewMode === 'grid' ? 'primary' : 'secondary'}
            onClick={() => setViewMode('grid')}
            accessibilityLabel="Grid view"
          />
          <Button
            icon={ListBulletedIcon}
            variant={viewMode === 'list' ? 'primary' : 'secondary'}
            onClick={() => setViewMode('list')}
            accessibilityLabel="List view"
          />
        </InlineStack>
      )}

      {viewMode === 'grid' ? (
        <InlineGrid columns={columns} gap="400">
          {products.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              onAddToWishlist={onAddToWishlist}
            />
          ))}
        </InlineGrid>
      ) : (
        <BlockStack gap="400">
          {products.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              onAddToWishlist={onAddToWishlist}
            />
          ))}
        </BlockStack>
      )}
    </BlockStack>
  );
}
